/**
 * Lightweight n-gram language detection, en/tr only (PRD §6.3). Character
 * bigram profiles hand-built from common word structure of each language,
 * plus decisive single characters (Turkish ı/ğ/ş İ never appear in English
 * text). Scores are summed log-ish weights over the first N characters; the
 * detector returns 'en' | 'tr' with 'en' as tie fallback. Deliberately
 * small: auto-detection is advisory — the per-document manual override in
 * the Reader is the correctness escape hatch (PRD §6.3).
 */

const SAMPLE_CHARS = 4000;

/** Characters that essentially decide the matter on their own. */
const TR_STRONG = /[ığşİĞŞ]/u;

const TR_BIGRAMS: readonly string[] = [
  'la',
  'ar',
  'an',
  'er',
  'in',
  'ın',
  'le',
  'de',
  'da',
  'ir',
  'iy',
  'ük',
  'ıl',
  'ör',
  'ış',
  'ği',
  'ğa',
  'şı',
  'çı',
  'ça',
  'ün',
  'ği',
  'yo',
  'uğ',
  'ml',
  'nl',
  'rl',
  'dı',
  'ti',
  'si',
];

const EN_BIGRAMS: readonly string[] = [
  'th',
  'he',
  'in',
  'er',
  'an',
  're',
  'on',
  'at',
  'en',
  'nd',
  'ti',
  'es',
  'or',
  'te',
  'of',
  'ed',
  'is',
  'it',
  'al',
  'as',
  'wh',
  'ng',
  'sh',
  'ou',
  'ea',
  'io',
  'ck',
  'gh',
  'ay',
  'ee',
];

const TR_WORDS = new Set([
  've',
  'bir',
  'bu',
  'için',
  'ile',
  'olarak',
  'daha',
  'çok',
  'gibi',
  'ama',
  'de',
  'da',
  'ne',
  'olan',
]);
const EN_WORDS = new Set([
  'the',
  'and',
  'of',
  'to',
  'in',
  'is',
  'that',
  'for',
  'with',
  'as',
  'was',
  'are',
  'this',
  'it',
]);

export function detectLanguage(text: string): 'en' | 'tr' {
  const sample = text.slice(0, SAMPLE_CHARS).toLowerCase();
  let tr = 0;
  let en = 0;

  // Decisive characters: each occurrence is heavy evidence.
  for (const ch of sample) {
    if (TR_STRONG.test(ch)) tr += 6;
    else if (ch === 'w' || ch === 'x' || ch === 'q') en += 3; // absent from Turkish orthography
  }

  const trSet = new Set(TR_BIGRAMS);
  const enSet = new Set(EN_BIGRAMS);
  for (let i = 0; i < sample.length - 1; i++) {
    const bigram = sample.slice(i, i + 2);
    if (trSet.has(bigram)) tr += 1;
    if (enSet.has(bigram)) en += 1;
  }

  for (const word of sample.split(/[^\p{L}]+/u)) {
    if (TR_WORDS.has(word)) tr += 8;
    if (EN_WORDS.has(word)) en += 8;
  }

  return tr > en ? 'tr' : 'en';
}
